import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';

const SubscriptionTab = ({ subscription, organization }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [usageMetrics, setUsageMetrics] = useState({ teamMembers: 0, activeProjects: 0 });
  const [metricsLoading, setMetricsLoading] = useState(true);

  React.useEffect(() => {
    if (organization?.id) {
      loadUsageMetrics(organization?.id);
    } else {
      setMetricsLoading(false);
    }
  }, [organization?.id]);

  const loadUsageMetrics = async (orgId) => {
    setMetricsLoading(true);
    try {
      const [teamResult, projectResult] = await Promise.all([
        supabase?.from('user_profiles')?.select('*', { count: 'exact', head: true })?.eq('organization_id', orgId)?.eq('status', 'active'),
        supabase?.from('projects')?.select('*', { count: 'exact', head: true })?.eq('organization_id', orgId)?.neq('phase', 'completed')
      ]);
      setUsageMetrics({
        teamMembers: teamResult?.count || 0,
        activeProjects: projectResult?.count || 0
      });
    } catch (err) {
      console.error('Error loading usage metrics:', err);
    } finally {
      setMetricsLoading(false);
    }
  };

  const handleManageBilling = async () => {
    setLoading(true);
    try {
      // Support both snake_case (DB) and camelCase field names
      const customerId = subscription?.stripe_customer_id || subscription?.stripeCustomerId;

      if (!customerId) {
        alert('No Stripe customer ID found. Please contact support.');
        return;
      }

      // Call Supabase Edge Function to create customer portal session
      const { data, error } = await supabase?.functions?.invoke('create-portal-session', {
        body: { customerId }
      });

      if (error) throw error;

      if (data?.url) {
        window.open(data?.url, '_blank');
      }
    } catch (error) {
      console.error('Error creating portal session:', error);
      alert('Failed to open billing portal. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { color: 'bg-success/10 text-success', label: 'Active' },
      trialing: { color: 'bg-accent/10 text-accent', label: 'Trial' },
      past_due: { color: 'bg-warning/10 text-warning', label: 'Past Due' },
      canceled: { color: 'bg-error/10 text-error', label: 'Canceled' },
      incomplete: { color: 'bg-muted text-muted-foreground', label: 'Incomplete' }
    };
    return statusConfig?.[status] || statusConfig?.incomplete;
  };

  // Support both snake_case (DB) and camelCase field names
  const subscriptionStatus = subscription?.subscription_status || subscription?.subscriptionStatus;
  const currentPeriodStart = subscription?.current_period_start || subscription?.currentPeriodStart;
  const currentPeriodEnd = subscription?.current_period_end || subscription?.currentPeriodEnd;
  const cancelAtPeriodEnd = subscription?.cancel_at_period_end || subscription?.cancelAtPeriodEnd;
  const stripeCustomerId = subscription?.stripe_customer_id || subscription?.stripeCustomerId;

  const statusBadge = getStatusBadge(subscriptionStatus);

  return (
    <div className="space-y-6">
      {/* Current Plan */}
      <div className="bg-card rounded-xl p-6 shadow-sm">
        <div className="flex items-start justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-foreground mb-2">Current Plan</h3>
            <p className="text-sm text-muted-foreground">Manage your subscription and billing</p>
          </div>
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusBadge?.color}`}>
            {statusBadge?.label}
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div>
              <p className="text-sm text-muted-foreground mb-1">Plan Type</p>
              <p className="text-base font-medium text-foreground capitalize">
                {subscription?.plan || 'Monthly'}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground mb-1">Billing Cycle</p>
              <p className="text-base font-medium text-foreground">
                {subscription?.plan === 'monthly' ? 'Monthly' : 'Yearly'}
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <p className="text-sm text-muted-foreground mb-1">Current Period Start</p>
              <p className="text-base font-medium text-foreground">
                {formatDate(currentPeriodStart)}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground mb-1">Next Payment Date</p>
              <p className="text-base font-medium text-foreground">
                {formatDate(currentPeriodEnd)}
              </p>
            </div>
          </div>
        </div>

        {cancelAtPeriodEnd && (
          <div className="mt-6 p-4 bg-warning/10 border border-warning/20 rounded-lg">
            <div className="flex items-start gap-3">
              <Icon name="AlertTriangle" size={20} className="text-warning mt-0.5" />
              <div>
                <p className="text-sm font-medium text-warning mb-1">Subscription Ending</p>
                <p className="text-sm text-muted-foreground">
                  Your subscription will end on {formatDate(currentPeriodEnd)}
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="mt-6 pt-6 border-t border-border">
          <Button
            onClick={handleManageBilling}
            disabled={loading || !stripeCustomerId}
            className="w-full md:w-auto"
          >
            {loading ? (
              <>
                <Icon name="Loader2" size={18} className="animate-spin" />
                <span>Loading...</span>
              </>
            ) : (
              <>
                <Icon name="ExternalLink" size={18} />
                <span>Manage Billing</span>
              </>
            )}
          </Button>
          <p className="text-xs text-muted-foreground mt-2">
            You'll be redirected to Stripe's secure portal to manage your subscription
          </p>
          {!stripeCustomerId && (
            <p className="text-xs text-warning mt-1">
              No active subscription found. Please subscribe to access billing management.
            </p>
          )}
        </div>
      </div>
      {/* Usage Metrics */}
      <div className="bg-card rounded-xl p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-foreground mb-4">Usage Metrics</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-4 bg-muted/50 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="Users" size={18} className="text-primary" />
              <p className="text-sm font-medium text-foreground">Team Members</p>
            </div>
            {metricsLoading ? (
              <div className="animate-pulse h-8 w-12 bg-muted rounded"></div>
            ) : (
              <p className="text-2xl font-bold text-foreground">{usageMetrics?.teamMembers}</p>
            )}
            <p className="text-xs text-muted-foreground mt-1">active members</p>
          </div>
          <div className="p-4 bg-muted/50 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="Briefcase" size={18} className="text-primary" />
              <p className="text-sm font-medium text-foreground">Active Projects</p>
            </div>
            {metricsLoading ? (
              <div className="animate-pulse h-8 w-12 bg-muted rounded"></div>
            ) : (
              <p className="text-2xl font-bold text-foreground">{usageMetrics?.activeProjects}</p>
            )}
            <p className="text-xs text-muted-foreground mt-1">in progress</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionTab;